import {useEffect, useRef, useState} from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';
import * as store from '@/store';
import {IPageSecurityParams, Props} from '@/interface';
import {loadWaterMarkSdk} from '@/utils/waterMarkUtils';
import sdk from '@/sdk';
import eventBus, {DOCUMENT_INIT_COMPLETE} from '@/eventBus';
import {PageSecurityIsSelected} from '@/Constant';

interface WaterMarkProps extends Props {
    docReady: boolean;
    isDocPage: boolean;
    repository: Repository | undefined;
    docGuid: string | undefined;
    curDocument?: WizDocument;
    pageSecuritySettings?: any;
    kuSecuritySettings?: any;
    hideWaterMark?: boolean;
    setHideWaterMark?: any;
}

export interface IPageSecurityWithWaterMark extends IPageSecurityParams {
    docGuid?: string;
    repositoryGuid?: string;
}

const WaterMark = (props: WaterMarkProps) => {
    const {
        docReady, pageSecuritySettings, kuSecuritySettings,
        repository, docGuid, isDocPage, hideWaterMark, setHideWaterMark,
    } = props;
    const [documentInit, setDocumentInit] = useState(false);

    useEffect(() => {
        return () => {
            setHideWaterMark(false);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docGuid]);

    // 监听文档是否初始化完成，初始化完成后再加载水印防止影响性能
    useEffect(() => {
        function handleDocumentInit() {
            setDocumentInit(true);
        }
        eventBus.on(DOCUMENT_INIT_COMPLETE, handleDocumentInit);
        return () => {
            eventBus.off(DOCUMENT_INIT_COMPLETE, handleDocumentInit);
        };
    }, []);

    // 先获取知识库安全设置
    useEffect(() => {
        if (!repository) {
            return;
        }
        const getKuSecuritySettings = async () => {
            const {repositoryGuid} = repository;
            // 非文档页，获取知识库安全设置
            if (!kuSecuritySettings || kuSecuritySettings.repositoryGuid !== repositoryGuid) {
                try {
                    const ksServer = await sdk.getKsServer(repository);
                    const res = await ksServer.getKuSecuritySetting();
                    const newSetting = {
                        ...res,
                        repositoryGuid,
                    };
                    store.setKuSecuritySettings(newSetting);
                    store.setPageSecuritySettings(newSetting);
                } catch (err) {
                    store.setKuSecuritySettings(undefined);
                    store.setPageSecuritySettings(undefined);
                }
            }
        };
        getKuSecuritySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [repository]);

    // 知识库获取完成后，再获取文档安全设置
    useEffect(() => {
        if (!repository || !kuSecuritySettings) {
            return;
        }
        const getSecuritySettings = async () => {
            if (!docGuid) {
                kuSecuritySettings && store.setPageSecuritySettings(kuSecuritySettings);
                return;
            }
            // 文档页，获取文档安全设置
            if (kuSecuritySettings.docGuid !== docGuid) {
                try {
                    const ksServer = await sdk.getKsServer(repository);
                    const res = await ksServer.getDocSecuritySetting({docGuid});
                    const newSetting = {
                        ...kuSecuritySettings,
                        ...res,
                        docGuid,
                    };
                    store.setPageSecuritySettings(newSetting);
                } catch (err) {
                    store.setPageSecuritySettings(undefined);
                }
            }
        };
        getSecuritySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docGuid, kuSecuritySettings]);

    const repositoryGuidCache = useRef('');
    useEffect(() => {
        if (!docReady || !repository || !pageSecuritySettings) {
            return;
        }
        const {repositoryGuid} = repository;

        const getWaterMarkShowOrHide = async () => {
            const {repositoryGuid} = repository;
            if (pageSecuritySettings && pageSecuritySettings.repositoryGuid === repositoryGuid) {
                const showWaterMark = pageSecuritySettings && PageSecurityIsSelected.includes(
                    pageSecuritySettings.watermarkStatus
                );
                setHideWaterMark(!showWaterMark);
            } else {
                setHideWaterMark(true);
            }
        };
        // 如果是文档页面，需等文档初始化完成再加载水印
        if (docGuid && documentInit || !docGuid) {
            repositoryGuidCache.current = repository?.repositoryGuid || '';
            loadWaterMarkSdk(pageSecuritySettings, repositoryGuid, docGuid ? 'docs' : '');
            // 控制水印是否展示
            getWaterMarkShowOrHide();
        }
    }, [docReady, docGuid, documentInit, pageSecuritySettings, repository, isDocPage, setHideWaterMark]);

    return (
        <div
            id="waterMarkContainer"
            className={classNames(styles.waterMarkContainer, hideWaterMark && styles.hideWaterMark)}
        ></div>
    );
};
export default store.connect([
    store.KEYS.PAGE_SECURITY_SETTINGS,
    store.KEYS.KU_SECURITY_SETTINGS,
])(WaterMark);

